<template>
  <div class="layer-tower-editor">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <h6 class="mb-0 text-dark">
        <i class="fas fa-layer-group mr-1"></i>Layer Tower
      </h6>
      <b-btn variant="outline-primary" size="sm" @click="addLayer">
        <i class="fas fa-plus mr-1"></i>Add Layer
      </b-btn>
    </div>

    <!-- Layer List -->
    <div v-if="localLayers.length === 0" class="text-muted p-3 border rounded bg-light mb-3">
      <i class="fas fa-info-circle mr-2"></i>No layers defined. Click "Add Layer" to add one.
    </div>

    <div
      v-for="(layer, index) in sortedLayers"
      :key="layer.id || index"
      class="border rounded mb-2"
      :class="editingIndex === index ? 'border-primary' : ''"
    >
      <!-- Layer Summary Row -->
      <div class="d-flex align-items-center justify-content-between p-3">
        <div class="flex-grow-1">
          <strong>{{ layer.layerName || ('Layer ' + (index + 1)) }}</strong>
          <span class="text-muted ml-2 small">
            Attachment: {{ formatCurrency(layer.attachmentPoint) }} &nbsp;|&nbsp;
            Limit: {{ formatCurrency(layer.layerLimit) }} &nbsp;|&nbsp;
            {{ (layer.participations && layer.participations.length) || 0 }} participation(s)
          </span>
        </div>
        <div class="d-flex align-items-center">
          <b-btn
            variant="link"
            size="sm"
            class="p-1"
            :disabled="index === 0"
            @click="moveLayer(index, -1)"
            title="Move Up"
          >
            <i class="fas fa-arrow-up"></i>
          </b-btn>
          <b-btn
            variant="link"
            size="sm"
            class="p-1"
            :disabled="index === localLayers.length - 1"
            @click="moveLayer(index, 1)"
            title="Move Down"
          >
            <i class="fas fa-arrow-down"></i>
          </b-btn>
          <b-btn
            variant="link"
            size="sm"
            class="p-1 text-primary"
            @click="toggleEdit(index)"
          >
            <i :class="editingIndex === index ? 'fas fa-chevron-up' : 'fas fa-edit'"></i>
            {{ editingIndex === index ? 'Collapse' : 'Edit' }}
          </b-btn>
          <b-btn
            variant="link"
            size="sm"
            class="p-1 text-danger"
            @click="deleteLayer(index)"
          >
            <i class="fas fa-trash"></i>
          </b-btn>
        </div>
      </div>

      <!-- Layer Edit Form (inline expansion) -->
      <div v-if="editingIndex === index" class="border-top p-3 bg-light">
        <div class="row">
          <!-- Layer Name -->
          <div class="col-lg-4 col-md-6 col-12">
            <b-form-group>
              <label class="control-label">Layer Name <small class="text-muted">(optional)</small></label>
              <b-form-input
                type="text"
                placeholder="Layer Name (auto-generated if blank)"
                :value="editBuffer.layerName"
                @input="editBuffer.layerName = $event"
              ></b-form-input>
            </b-form-group>
          </div>

          <!-- Attachment Point -->
          <div class="col-lg-4 col-md-6 col-12">
            <b-form-group>
              <label class="control-label">Attachment Point ($)</label>
              <b-form-input
                type="number"
                placeholder="0"
                :value="editBuffer.attachmentPoint"
                @input="editBuffer.attachmentPoint = parseFloat($event) || 0"
                min="0"
                step="1000"
              ></b-form-input>
            </b-form-group>
          </div>

          <!-- Layer Limit -->
          <div class="col-lg-4 col-md-6 col-12">
            <b-form-group>
              <label class="control-label">Layer Limit ($) <span class="text-danger">*</span></label>
              <b-form-input
                type="number"
                placeholder="0"
                :value="editBuffer.layerLimit"
                @input="editBuffer.layerLimit = parseFloat($event) || 0"
                min="0"
                step="1000"
                :state="editBuffer.layerLimit > 0 ? true : false"
              ></b-form-input>
              <small v-if="!(editBuffer.layerLimit > 0)" class="text-danger">Layer Limit is required and must be greater than 0.</small>
            </b-form-group>
          </div>
        </div>

        <!-- Participations -->
        <div class="mb-3">
          <label class="control-label font-weight-bold">Participations</label>

          <!-- Multi-lead warning -->
          <div v-if="multipleLeadsWarning" class="alert alert-warning py-2 mb-2">
            <i class="fas fa-exclamation-triangle mr-1"></i>
            Warning: Multiple participations have the Lead role. Only one Lead is typically expected.
          </div>

          <!-- Participation total validation -->
          <div class="mb-2">
            <span :class="participationTotalClass">
              Total: {{ participationTotal }}%
              <span v-if="participationTotal === 100"> &#10003;</span>
              <span v-if="participationTotal > 100"> &#9888; Must equal 100%</span>
              <span v-if="participationTotal < 100 && editBuffer.participations && editBuffer.participations.length > 0"> &#9888; Must equal 100%</span>
            </span>
          </div>

          <layer-participation-editor
            :participations="editBuffer.participations"
            :policies="policies"
            :layer-limit="editBuffer.layerLimit"
            @input="editBuffer.participations = $event"
          ></layer-participation-editor>
        </div>

        <!-- Save / Cancel -->
        <div class="d-flex justify-content-end mt-2">
          <b-btn variant="secondary" size="sm" class="mr-2" @click="cancelEdit">Cancel</b-btn>
          <b-btn
            variant="primary"
            size="sm"
            @click="saveLayer"
            :disabled="!isEditBufferDirty || !(editBuffer.layerLimit > 0)"
          >
            Save Layer
          </b-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LayerParticipationEditor from './LayerParticipationEditor.vue'

export default {
  name: 'LayerTowerEditor',
  components: {
    LayerParticipationEditor
  },
  props: {
    value: {
      type: Array,
      default: function () { return [] }
    },
    policies: {
      type: Array,
      default: function () { return [] }
    }
  },
  data: function () {
    return {
      localLayers: [],
      editingIndex: null,
      editBuffer: this.emptyLayer(),
      originalLayerSnapshot: null
    }
  },
  computed: {
    sortedLayers: function () {
      return this.localLayers.slice().sort(function (a, b) {
        return (a.sortOrder || 0) - (b.sortOrder || 0)
      })
    },
    participationTotal: function () {
      var parts = (this.editBuffer && this.editBuffer.participations) ? this.editBuffer.participations : []
      var total = 0
      for (var i = 0; i < parts.length; i++) {
        total += parseFloat(parts[i].participationPercent) || 0
      }
      return Math.round(total * 10000) / 100
    },
    participationTotalClass: function () {
      if (this.participationTotal === 100) return 'text-success font-weight-bold'
      if (this.participationTotal > 100) return 'text-danger font-weight-bold'
      return 'text-warning font-weight-bold'
    },
    multipleLeadsWarning: function () {
      var parts = (this.editBuffer && this.editBuffer.participations) ? this.editBuffer.participations : []
      var leadCount = 0
      for (var i = 0; i < parts.length; i++) {
        if (parts[i].role === 1) leadCount++
      }
      return leadCount > 1
    },
    isEditBufferDirty: function () {
      if (!this.originalLayerSnapshot) return false
      return JSON.stringify(this.editBuffer) !== this.originalLayerSnapshot
    }
  },
  watch: {
    value: function (newVal) {
      this.localLayers = newVal ? newVal.slice() : []
    }
  },
  created: function () {
    this.localLayers = this.value ? this.value.slice() : []
  },
  methods: {
    emptyLayer: function () {
      return {
        id: null,
        layerName: '',
        attachmentPoint: 0,
        layerLimit: 0,
        sortOrder: 0,
        participations: []
      }
    },
    addLayer: function () {
      var maxOrder = 0
      for (var i = 0; i < this.localLayers.length; i++) {
        if ((this.localLayers[i].sortOrder || 0) >= maxOrder) {
          maxOrder = (this.localLayers[i].sortOrder || 0) + 1
        }
      }
      var newLayer = this.emptyLayer()
      newLayer.sortOrder = maxOrder
      this.localLayers.push(newLayer)
      this.editingIndex = this.localLayers.length - 1
      this.editBuffer = Object.assign({}, newLayer, { participations: [] })
      this.originalLayerSnapshot = JSON.stringify(this.editBuffer)
      this.$emit('input', this.localLayers.slice())
    },
    toggleEdit: function (index) {
      if (this.editingIndex === index) {
        this.cancelEdit()
        return
      }
      this.editingIndex = index
      var layer = this.sortedLayers[index]
      this.editBuffer = Object.assign({}, layer, {
        participations: (layer.participations || []).slice()
      })
      this.originalLayerSnapshot = JSON.stringify(this.editBuffer)
    },
    cancelEdit: function () {
      this.editingIndex = null
      this.editBuffer = this.emptyLayer()
      this.originalLayerSnapshot = null
    },
    saveLayer: function () {
      if (!(this.editBuffer.layerLimit > 0)) return
      var sorted = this.sortedLayers
      var targetLayer = sorted[this.editingIndex]
      // Find in localLayers by reference match
      var localIdx = -1
      for (var i = 0; i < this.localLayers.length; i++) {
        if (this.localLayers[i] === targetLayer) { localIdx = i; break }
      }
      if (localIdx === -1) {
        // fallback: find by sortOrder match
        for (var j = 0; j < this.localLayers.length; j++) {
          if (this.localLayers[j].sortOrder === targetLayer.sortOrder) { localIdx = j; break }
        }
      }
      if (localIdx >= 0) {
        var updated = Object.assign({}, this.editBuffer)
        this.localLayers.splice(localIdx, 1, updated)
      }
      this.$emit('input', this.localLayers.slice())
      this.editingIndex = null
      this.editBuffer = this.emptyLayer()
    },
    deleteLayer: function (index) {
      var layer = this.sortedLayers[index]
      var localIdx = -1
      for (var i = 0; i < this.localLayers.length; i++) {
        if (this.localLayers[i] === layer) { localIdx = i; break }
      }
      if (localIdx >= 0) {
        this.localLayers.splice(localIdx, 1)
      }
      if (this.editingIndex === index) {
        this.editingIndex = null
        this.editBuffer = this.emptyLayer()
      }
      this.$emit('input', this.localLayers.slice())
    },
    moveLayer: function (index, direction) {
      var sorted = this.sortedLayers
      var targetIndex = index + direction
      if (targetIndex < 0 || targetIndex >= sorted.length) return

      var layerA = sorted[index]
      var layerB = sorted[targetIndex]

      // Swap sortOrders
      var tempOrder = layerA.sortOrder
      layerA.sortOrder = layerB.sortOrder
      layerB.sortOrder = tempOrder

      this.$emit('input', this.localLayers.slice())
    },
    formatCurrency: function (val) {
      if (val === null || val === undefined || val === '') return '$0'
      return '$' + parseFloat(val).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    }
  }
}
</script>
